/**
 * Custom Element Base
 *
 * HTMLParsedElement - Used by other classes, safe to ignore this one.
 * GWElement - Primary base class for custom elements. Use this instead of extending HTMLElement.
 * GWObservedElement - GWElement with additional observer support. Simplifies implementation of observers in custom elements.
 */

// Customized version of HTMLParsedElement by WebReflection (https://github.com/WebReflection/html-parsed-element/tree/master)
// Added `reverse` param to initialize children before their parents

/**
 * @param {boolean} [reverse=false] - if true, the queue will be processed in reverse order
 */
export const HTMLParsedElement = ((reverse = false) => {
  const DCL = 'DOMContentLoaded'
  const init = new WeakMap()
  const queue = []
  const isParsed = el => {
    do {
      if (el.nextSibling) return true
    } while ((el = el.parentNode))
    return false
  }
  const upgrade = () => {
    if (reverse) queue.reverse()

    queue.splice(0).forEach(info => {
      if (init.get(info[0]) !== true) {
        init.set(info[0], true)
        info[0][info[1]]()
      }
    })
  }
  document.addEventListener(DCL, upgrade)
  class HTMLParsedElement extends HTMLElement {
    static withParsedCallback(Class, name = 'parsed') {
      const { prototype } = Class
      const { connectedCallback } = prototype
      const method = name + 'Callback'
      const cleanUp = (el, observer, ownerDocument, onDCL) => {
        observer.disconnect()
        ownerDocument.removeEventListener(DCL, onDCL)
        parsedCallback(el)
      }
      const parsedCallback = el => {
        if (!queue.length) requestAnimationFrame(upgrade)
        queue.push([el, method])
      }
      Object.defineProperties(prototype, {
        connectedCallback: {
          configurable: true,
          writable: true,
          value() {
            if (connectedCallback) connectedCallback.apply(this, arguments)
            if (method in this && !init.has(this)) {
              const self = this
              const { ownerDocument } = self
              init.set(self, false)
              if (ownerDocument.readyState === 'complete' || isParsed(self))
                parsedCallback(self)
              else {
                const onDCL = () =>
                  cleanUp(self, observer, ownerDocument, onDCL)
                ownerDocument.addEventListener(DCL, onDCL)
                const observer = new MutationObserver(() => {
                  /* istanbul ignore else */
                  if (isParsed(self))
                    cleanUp(self, observer, ownerDocument, onDCL)
                })
                observer.observe(self.parentNode, {
                  childList: true,
                  subtree: true,
                })
              }
            }
          },
        },
        [name]: {
          configurable: true,
          get() {
            return init.get(this) === true
          },
        },
      })
      return Class
    }
  }
  return HTMLParsedElement.withParsedCallback(HTMLParsedElement)
})()